/*
 * AccountDetails
 *
 * Features:
 * - Renders schema-driven account detail sections with permission-aware editing
 * - Persists account changes through the account data route
 * - Supports delete and cancel flows for account management
 *
 * Implementation:
 * - Only editable schemas can submit updates
 * - Save requests preserve the current account name when the active schema section
 *   does not include a name field
 * - Notifications communicate update and delete outcomes to the user
 *
 * Changes:
 * - 2026-08-18: Removes nested Overview scrolling in favor of the application page scrollbar
 * - 2026-09-04: Shows the permission-gated Franchise form from every RLP Account Overview path
 * - 2026-08-18: Restricts the Franchise Account overview switch to its Admin team and enables authorized edits
 * - 2026-08-06: Added cache-coherent update/delete mutations and router navigation
 * - 2026-07-29: Replaced stacked paired RLP/Franchise forms with an account-scoped
 *   segmented switch in a stable schema-card header, preserving keyboard focus
 * - 2026-04-28: Prevented schema section saves from blanking account names when
 *   the submitted payload omits the name field
 * - 2026-08-25: Resolves legacy RLP CompanyID fields from normalized account data
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Account } from '../../models/Account';
import { THEME_CLASSES } from '../../constants/themeConstants';
import SchemaBasedDetails from '../common/SchemaBasedDetails';
import { useNotification } from '../../context/NotificationContext';
import { canDelete, canEdit } from '../../utils/permissions';
import { useFilteredSchemas } from '../../hooks/useFilteredSchemas';
import { useTeams } from '../../context/TeamContext';
import { PERMISSION_MESSAGES } from '../../constants/messages';
import { useDeleteAccountMutation, useUpdateAccountMutation } from '../../query/accountQueries';
import type { SchemaWithPermissions } from '../../context/SchemaContext';

interface AccountDetailsProps {
  account: Account;
}

type PairedAccountForm = 'rlp' | 'franchise';

interface AccountFormSelection {
  accountId: number;
  form: PairedAccountForm;
}

const normalizeRlpCompanyIdField = <T extends SchemaWithPermissions>(schema: T): T => {
  if (schema.name.trim().toLowerCase() !== 'rlp account') return schema;

  try {
    const fields = JSON.parse(schema.schema);
    if (!Array.isArray(fields)) return schema;

    return {
      ...schema,
      schema: JSON.stringify(fields.map((field) =>
        field?.name === 'companyID'
          ? { ...field, name: 'companyId' }
          : field
      )),
    } as T;
  } catch {
    return schema;
  }
};

const AccountDetails: React.FC<AccountDetailsProps> = ({ account }) => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { currentUserId, teams } = useTeams();
  const updateAccount = useUpdateAccountMutation();
  const deleteAccount = useDeleteAccountMutation();
  const { schemas: filteredSchemas, isLoading } = useFilteredSchemas(2, account); // Type 2 for accounts
  const [formSelection, setFormSelection] = useState<AccountFormSelection>({
    accountId: account.id,
    form: 'rlp',
  });

  // Check permissions using the new utility
  const hasDeletePermission = canDelete(account, currentUserId);
  const hasAccountEditPermission = canEdit(account, teams, currentUserId);
  const accessibleSchemas = filteredSchemas.flatMap((schema) => {
    const isFranchiseAccount = schema.name.trim().toLowerCase() === 'franchise account';
    if (!isFranchiseAccount) {
      return [schema];
    }

    const hasFranchiseAccess = teams.some((team) => team.teamId === schema.teamId);
    return hasFranchiseAccess
      ? [{ ...schema, isEditable: hasAccountEditPermission }]
      : [];
  });
  const formSchemas = accessibleSchemas.map(normalizeRlpCompanyIdField);
  const hasEditPermission = formSchemas.some(s => s.isEditable);
  const rlpAccountSchema = formSchemas.find((schema) => schema.name.trim().toLowerCase() === 'rlp account');
  const franchiseAccountSchema = formSchemas.find((schema) => schema.name.trim().toLowerCase() === 'franchise account');
  const hasPairedAccountForms = Boolean(rlpAccountSchema && franchiseAccountSchema);
  const activePairedForm: PairedAccountForm = formSelection.accountId === account.id
    ? formSelection.form
    : 'rlp';
  const unaffectedSchemas = hasPairedAccountForms
    ? formSchemas.filter((schema) => schema !== rlpAccountSchema && schema !== franchiseAccountSchema)
    : formSchemas;
  const activePairedSchema = activePairedForm === 'franchise'
    ? franchiseAccountSchema
    : rlpAccountSchema;
  const accountFormData: Account = {
    ...account,
    companyId: account.companyId ?? account.rlpCompanyId,
  };

  if (isLoading) {
    return <div>Loading schemas...</div>;
  }

  const handleSubmit = async (data: any) => {
    try {
      await updateAccount.mutateAsync({
        id: account.id,
        name: typeof data.name === 'string' && data.name.trim() !== ''
          ? data.name
          : account.name,
        data: JSON.stringify(data),
      });
      showNotification('Account updated successfully', 'success');
    } catch (error) {
      console.error('Error updating account:', error);
      showNotification('Failed to update account', 'error');
      throw error;
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this account? This action cannot be undone.')) {
      try {
        await deleteAccount.mutateAsync({ id: account.id });
        showNotification('Account deleted successfully', 'success');
        navigate('/accounts');
      } catch (error) {
        console.error('Error deleting account:', error);
        showNotification('Failed to delete account', 'error');
      }
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      window.location.reload();
    }
  };

  const accountFormSwitcher = (
    <div
      className={`mx-auto inline-flex h-9 w-full max-w-md rounded-xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} p-1 shadow-sm`}
      role="group"
      aria-label="Account form"
    >
      {([
        { id: 'rlp', label: 'RLP Account' },
        { id: 'franchise', label: 'Franchise Account' },
      ] as const).map((option) => {
        const isSelected = activePairedForm === option.id;

        return (
          <button
            key={option.id}
            type="button"
            onClick={() => setFormSelection({ accountId: account.id, form: option.id })}
            className={`min-w-0 flex-1 rounded-lg px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 ${isSelected
              ? `${THEME_CLASSES.button.primary} shadow-sm`
              : `${THEME_CLASSES.text.secondary} hover:bg-slate-100 dark:hover:bg-slate-900`
              }`}
            aria-pressed={isSelected}
          >
            <span className="block truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div>
      {/* Permission indicator - only show for limited access */}
      {!hasEditPermission && !account.rlpCompanyId && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-yellow-800 font-medium">
              {PERMISSION_MESSAGES.LIMITED_ACCESS}
            </span>
          </div>
        </div>
      )}

      {hasPairedAccountForms && activePairedSchema ? (
        <div data-account-schema-details="true">
          {unaffectedSchemas.length > 0 && (
            <SchemaBasedDetails
              key={`account-details-unaffected-${account.id}`}
              schemas={unaffectedSchemas}
              data={accountFormData}
              onSubmit={handleSubmit}
              onDelete={handleDelete}
              onCancel={handleCancel}
              isDeleteEnabled={hasDeletePermission}
              isDisabled={(schema: any) => !schema.isEditable}
              constrainHeight={false}
              constrainFieldGrid={false}
            />
          )}

          <div data-account-form-panel="true">
            <SchemaBasedDetails
              key={`account-details-paired-${account.id}`}
              schemas={[activePairedSchema]}
              data={accountFormData}
              onSubmit={handleSubmit}
              onDelete={handleDelete}
              onCancel={handleCancel}
              isDeleteEnabled={hasDeletePermission}
              isDisabled={(schema: any) => !schema.isEditable}
              sectionHeaderContent={accountFormSwitcher}
              sectionsCollapsible={false}
              getSectionKey={() => 'paired-account-form'}
              constrainHeight={false}
              constrainFieldGrid={false}
            />
          </div>
        </div>
      ) : (
        <SchemaBasedDetails
          key={`account-details-${account.id}`}
          schemas={formSchemas}
          data={accountFormData}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          onCancel={handleCancel}
          isDeleteEnabled={hasDeletePermission}
          isDisabled={(schema: any) => !schema.isEditable}
          constrainHeight={false}
          constrainFieldGrid={false}
        />
      )}
    </div>
  );
};

export default AccountDetails;
